import React from 'react';
import {
  Package, Building, Boxes, MountainSnow, Layers3, Atom, Check, Info,
} from 'lucide-react';
import { useLang } from '../context/LanguageContext';
import { PageHeader } from '../components/PageHeader';
import { Reveal } from '../components/Reveal';
import ContactStrip from '../components/ContactStrip';
import SEO from '../components/SEO';
import { getSeo, SEO_KEYWORDS } from '../data/seo';
import { TILE_ADHESIVE, NORM_AREAS, TILE_CHART } from '../data/science';
import ScienceChart from '../components/ScienceChart';

const icons = [Package, Building, Boxes, MountainSnow, Layers3, Atom];

const Applications = () => {
  const { t, lang } = useLang();
  const groups = t('applications.groups');
  const seo = getSeo('applications', lang);

  return (
    <div>
      <SEO title={seo.title} description={seo.description} path="/anwendungen" lang={lang} keywords={SEO_KEYWORDS[lang]} />
      <PageHeader label={t('applications.label')} title={t('applications.title')} lead={t('applications.lead')} imageKey="concreteArch1" />
      <section className="bg-white py-16 lg:py-24">
        <div className="container-wide grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6">
          {groups.map((g, i) => {
            const Icon = icons[i] || Package;
            return (
              <Reveal key={i} delay={(i % 3) * 80}>
                <article className="h-full flex flex-col p-6 lg:p-7 rounded-xl bg-white border border-slate-200 hover:border-blue-300 hover:shadow-md hover:shadow-blue-700/5 transition">
                  <div className="flex items-center gap-3">
                    <div className="inline-flex items-center justify-center w-11 h-11 rounded-lg bg-blue-700/5 border border-blue-700/15 text-blue-700">
                      <Icon className="w-5 h-5" />
                    </div>
                    <h3 className="text-lg font-bold text-slate-900">{g.title}</h3>
                  </div>
                  <ul className="mt-5 space-y-2.5">
                    {g.items.map((it, j) => (
                      <li key={j} className="flex items-start gap-2.5 text-[14px] text-slate-600">
                        <Check className="w-4 h-4 text-blue-700 mt-0.5 shrink-0" />
                        <span>{it}</span>
                      </li>
                    ))}
                  </ul>
                </article>
              </Reveal>
            );
          })}
        </div>
      </section>

      {/* Documented application focus areas (norm context) */}
      <section className="bg-slate-50 border-y border-slate-200 py-14 lg:py-20">
        <div className="container-wide">
          <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-blue-700">{t('applications.normAreasTitle')}</p>
          <ul className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {NORM_AREAS[lang].map((area, i) => (
              <li key={i} className="flex items-start gap-2.5 rounded-lg bg-white border border-slate-200 px-4 py-3 text-[14px] text-slate-700">
                <Check className="w-4 h-4 text-blue-700 mt-0.5 shrink-0" />
                <span>{area}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Metakaolin in dry mortars & tile adhesives (product-neutral, verified) */}
      <section className="bg-white py-14 lg:py-20" data-testid="dry-mortar-section">
        <div className="container-wide">
          <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-blue-700">{t('applications.dryMortarLabel')}</p>
          <h2 className="mt-3 text-[26px] lg:text-[30px] font-extrabold tracking-tight text-slate-900 max-w-3xl">{t('applications.dryMortarTitle')}</h2>
          <p className="mt-4 text-[15px] text-slate-600 leading-relaxed max-w-3xl">{t('applications.dryMortarLead')}</p>
          <div className="mt-9 grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="rounded-xl border border-slate-200 bg-white p-6">
              <h3 className="text-[15px] font-bold text-slate-900">{t('applications.dryMortarBenefitsTitle')}</h3>
              <ul className="mt-4 space-y-2.5">
                {TILE_ADHESIVE.benefits[lang].map((b, i) => (
                  <li key={i} className="flex items-start gap-2.5 text-[14px] text-slate-600">
                    <Check className="w-4 h-4 text-blue-700 mt-0.5 shrink-0" />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-6">
              <h3 className="text-[15px] font-bold text-slate-900">{t('applications.dryMortarFactsTitle')}</h3>
              <ul className="mt-4 space-y-3 text-[13.5px] text-slate-600 leading-relaxed list-disc pl-4">
                {TILE_ADHESIVE.facts[lang].map((f, i) => (
                  <li key={i}>{f}</li>
                ))}
              </ul>
            </div>
          </div>
          <div className="mt-8">
            <Reveal>
              <ScienceChart
                title={t('applications.tileChartTitle')}
                unit={TILE_CHART.unit[lang]}
                categories={TILE_CHART.categories[lang]}
                series={TILE_CHART.series.map((s) => ({ label: s.label[lang], values: s.values, color: s.color }))}
                methodology={TILE_CHART.methodology[lang]}
                summary={t('applications.tileChartSummary')}
                tableLabel={t('common.parameter')}
              />
            </Reveal>
          </div>
          <div className="mt-6 flex items-start gap-3 rounded-xl border border-blue-200 bg-blue-50 px-5 py-4 max-w-4xl">
            <Info className="w-5 h-5 text-blue-700 shrink-0 mt-0.5" />
            <p className="text-[13px] text-slate-600 leading-relaxed">{t('applications.appDisclaimer')}</p>
          </div>
        </div>
      </section>
      <ContactStrip />
    </div>
  );
};

export default Applications;
